import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Container, Card, Alert } from 'react-bootstrap';
import { LogoUpload } from '../components/logo';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { UserStore } from '../stores/user-store';
import { LoadingStore } from '../stores/loading-store';

interface LogoUploadContainerProps {
  userProfileUIStore?: UserProfileUIStore;
  userStore?: UserStore;
  loadingStore?: LoadingStore;
}

interface LogoUploadContainerState {
  successMessage: string | null;
  errorMessage: string | null;
}

@inject('userProfileUIStore', 'userStore', 'loadingStore')
@observer
export class LogoUploadContainer extends React.Component<
  LogoUploadContainerProps,
  LogoUploadContainerState
> {
  state: LogoUploadContainerState = {
    successMessage: null,
    errorMessage: null,
  };

  componentDidMount() {
    const { userProfileUIStore, userStore } = this.props;

    // Load current user and user profile if not already loaded
    if (!userStore?.currentUser?.subject) {
      userStore?.loadCurrentUser();
    }

    if (!userProfileUIStore?.currentUserProfile) {
      userProfileUIStore?.loadCurrentUserProfile();
    }
  }

  handleLogoUploaded = async (logoUrl: string) => {
    const { userProfileUIStore } = this.props;

    if (!userProfileUIStore?.currentUserProfile) {
      this.setState({
        errorMessage: 'Error: user profile not loaded',
      });
      return;
    }

    try {
      // Update the user profile with the new logo URL using MobX action
      userProfileUIStore.updateLogo(logoUrl);

      // Save the updated profile
      await userProfileUIStore.saveCurrentUserProfile();

      this.setState({
        successMessage: 'Logo uploaded and saved successfully!',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error saving logo URL:', error);
      this.setState({
        errorMessage: 'Error saving logo. Please try again.',
        successMessage: null,
      });
    }
  };

  handleLogoRemoved = async () => {
    const { userProfileUIStore } = this.props;

    if (!userProfileUIStore?.currentUserProfile) {
      this.setState({
        errorMessage: 'Error: user profile not loaded',
      });
      return;
    }

    try {
      // Clear the logo URL from user profile using MobX action
      userProfileUIStore.removeLogo();

      // Save the updated profile
      await userProfileUIStore.saveCurrentUserProfile();

      this.setState({
        successMessage: 'Logo removed successfully!',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error removing logo:', error);
      this.setState({
        errorMessage: 'Error removing logo. Please try again.',
        successMessage: null,
      });
    }
  };

  handleError = (error: string) => {
    this.setState({
      errorMessage: error,
      successMessage: null,
    });
  };

  dismissAlert = (type: 'success' | 'error') => {
    if (type === 'success') {
      this.setState({ successMessage: null });
    } else {
      this.setState({ errorMessage: null });
    }
  };

  render() {
    const { userProfileUIStore } = this.props;
    const { successMessage, errorMessage } = this.state;

    if (!userProfileUIStore?.currentUserProfile) {
      return (
        <Container className="mt-4">
          <Card>
            <Card.Body>
              <div className="text-center">
                <p>Loading user profile...</p>
              </div>
            </Card.Body>
          </Card>
        </Container>
      );
    }

    return (
      <Container fluid className="mt-4">
        <Card>
          <Card.Header>Manage Logo</Card.Header>
          <Card.Body>
            {successMessage && (
              <Alert
                variant="success"
                dismissible
                onClose={() => this.dismissAlert('success')}
              >
                <div className="text-center m-2">{successMessage}</div>
              </Alert>
            )}

            {errorMessage && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => this.dismissAlert('error')}
              >
                <div className="text-center m-2">{errorMessage}</div>
              </Alert>
            )}

            <LogoUpload
              userProfile={userProfileUIStore.currentUserProfile}
              userProfileUIStore={userProfileUIStore}
              onLogoUploaded={this.handleLogoUploaded}
              onLogoRemoved={this.handleLogoRemoved}
              onError={this.handleError}
            />
          </Card.Body>
        </Card>
      </Container>
    );
  }
}
